import React, { Fragment, useState, useEffect } from 'react';
import {
  Box,
  Divider,
  Fade,
  FormControlLabel,
  IconButton,
  Modal,
  Switch,
  TextField,
  Typography,
  inputBaseClasses,
} from '@mui/material';
import { selectActiveProductById, selectedElement, updateContentElementById } from 'features/document/documentSlice';
import { isPricingTableElement } from 'utils/typeGuards';
import {
  hideColumnByName,
  parseQuoteLineItemsToPricingTable,
  showColumnByName,
} from 'pages/DocumentBuilder/components/ContentSection/components/ElementSection/components/PricingTable/PricingTable.utils';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ActiveProductForm } from './components/ActiveProductForm';
import Button from 'components/Button/Button';
import styles from './PricingTableSidebar.module.css';
import { CloseRounded } from '@mui/icons-material';
import { colorPalette, docGeniusPalette } from 'styles/partials/colors';
import { selectQuote, selectSalesforceId } from 'features/salesforce/salesforceSlice';
import { usePutProductInQuoteMutation } from 'features/salesforce/salesforceApi';
import { toast } from 'react-toastify';

interface PricingTableSidebarProps {
  currentElement: selectedElement;
}

const commonTextFieldStyles = {
  [`& .${inputBaseClasses.root}`]: {
    borderRadius: '8px',
    height: '48px',
  },
};

export default function PricingTableSidebar({ currentElement }: PricingTableSidebarProps) {
  const tableElement = currentElement.data;
  if (!isPricingTableElement(tableElement)) {
    return null;
  }

  const { pageId, rowId, columnId, data } = currentElement;

  const dispatch = useAppDispatch();
  const activeProduct = useAppSelector(state => selectActiveProductById(state, pageId, rowId, columnId, data.id));
  const salesforceId = useAppSelector(selectSalesforceId);
  const quote = useAppSelector(selectQuote);

  const [previewedProduct, setPreviewedProduct] = useState(activeProduct);
  const [openModal, setOpenModal] = useState(false);

  const { Name: quoteName } = quote;
  const tableContent = tableElement.content;
  const {
    products,
    hideDiscountColumn,
    hideListPriceColumn,
    hideSummary,
    headerColor,
    columns,
    hideDescriptionColumn,
    showDescriptionAsImage,
  } = tableContent;

  const [putProductInQuote, { isLoading: isSaveLoading }] = usePutProductInQuoteMutation();

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleOnClickDiscardChanges = () => {
    dispatch(
      updateContentElementById({
        content: {
          ...tableContent,
          activeProduct: null,
        },
        style: {},
      })
    );
    handleCloseModal();
  };

  const handleOnChangeHideListPrice = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    let newTable;
    const headerText = columns.find(col => col.id == 'column-2')?.header || 'List Price';

    if (!checked) {
      newTable = hideColumnByName(headerText, tableContent);
    } else {
      newTable = showColumnByName(headerText, tableContent);
    }

    dispatch(
      updateContentElementById({
        content: {
          ...newTable,
          hideListPriceColumn: !checked,
        },
        style: {},
      })
    );
  };

  const handleOnChangeHideDiscount = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    let newTable;
    const headerText = columns.find(col => col.id == 'column-5')?.header || 'Discount';

    if (!checked) {
      newTable = hideColumnByName(headerText, tableContent);
    } else {
      newTable = showColumnByName(headerText, tableContent);
    }

    dispatch(
      updateContentElementById({
        content: {
          ...newTable,
          hideDiscountColumn: !checked,
        },
        style: {},
      })
    );
  };

  const handleOnChangeHideDescription = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    let newTable;
    const headerText = columns.find(col => col.id == 'column-7')?.header || 'Description';

    console.log(headerText);

    if (!checked) {
      newTable = hideColumnByName(headerText, tableContent);
    } else {
      newTable = showColumnByName(headerText, tableContent);
    }

    dispatch(
      updateContentElementById({
        content: {
          ...newTable,
          hideDescriptionColumn: !checked,
        },
        style: {},
      })
    );
  };

  const handleOnChangeShowDescriptionAsAnImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    dispatch(
      updateContentElementById({
        content: {
          showDescriptionAsImage: !checked,
        },
        style: {},
      })
    );
  };

  const handleOnChangeHideSummary = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    dispatch(
      updateContentElementById({
        content: {
          ...tableContent,
          hideSummary: !checked,
        },
        style: {},
      })
    );
  };

  const handleOnChangeSalesPrice = (newSalesPrice: string) => {
    if (previewedProduct) {
      setPreviewedProduct({
        ...previewedProduct,
        UnitPrice: Number(newSalesPrice),
      });
    }
  };

  const handleOnChangeQuantity = (newQuantity: string) => {
    if (previewedProduct) {
      setPreviewedProduct({
        ...previewedProduct,
        Quantity: Number(newQuantity),
      });
    }
  };

  const handleOnChangeDiscount = (newDiscount: string) => {
    if (previewedProduct) {
      setPreviewedProduct({
        ...previewedProduct,
        Discount: Number(newDiscount),
      });
    }
  };

  const handleOnChangeDescription = (newDescription: string) => {
    if (previewedProduct) {
      setPreviewedProduct({
        ...previewedProduct,
        Description: newDescription,
      });
    }
  };

  const handleOnChangeBackgroundColor = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      updateContentElementById({
        content: {
          ...tableContent,
          headerColor: event.target.value,
        },
        style: {},
      })
    );
  };

  const handleOnChangeHeaderText = (newHeader: string, columnId: string) => {
    const newColumns = tableContent.columns.map(column => {
      if (column.id === columnId) {
        return { ...column, header: newHeader };
      }
      return column;
    });

    dispatch(
      updateContentElementById({
        content: {
          ...tableContent,
          columns: newColumns,
        },
        style: {},
      })
    );
  };

  const handleOnSave = async (replaceProductName: boolean) => {
    if (products && previewedProduct) {
      const body = {
        salesforceId: salesforceId,
        quoteId: quote.Id,
        product: {
          id: previewedProduct.Id,
          quantity: previewedProduct.Quantity,
          salesPrice: previewedProduct.UnitPrice,
          discount: previewedProduct.Discount || 0,
          lineItemDescription: previewedProduct.Description || '',
        },
      };
      try {
        await putProductInQuote(body).unwrap();
        toast.success(`The product "${previewedProduct.Name}" was updated successfully`, {
          position: toast.POSITION.BOTTOM_CENTER,
          style: {
            background: colorPalette.neutralGray3,
            color: '#F2F3F3',
            minWidth: '570px',
          },
        });
        const newProducts = products.map(product => {
          if (product.Id === previewedProduct.Id) {
            if (replaceProductName) {
              return {
                ...previewedProduct,
                Name: previewedProduct.Description,
              };
            }
            return previewedProduct;
          }
          return product;
        });

        const headerTitles = columns.map(column => column.header);
        const newTable = parseQuoteLineItemsToPricingTable(
          newProducts,
          quote.Tax,
          quote.ShippingHandling,
          headerColor,
          headerTitles,
          hideDiscountColumn,
          hideListPriceColumn,
          hideSummary,
          hideDescriptionColumn,
          showDescriptionAsImage
        );

        dispatch(
          updateContentElementById({
            content: newTable,
            style: {},
          })
        );
      } catch (error) {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    setPreviewedProduct(activeProduct);
  }, [activeProduct]);

  return (
    <Fragment>
      <Box alignItems={'flex-start'} display={'flex'} flexDirection={'column'}>
        {previewedProduct ? (
          <ActiveProductForm
            previewedProduct={previewedProduct}
            quoteName={quoteName}
            isSaveLoading={isSaveLoading}
            onChangeSalesPrice={handleOnChangeSalesPrice}
            onChangeQuantity={handleOnChangeQuantity}
            onChangeDiscount={handleOnChangeDiscount}
            onChangeDescription={handleOnChangeDescription}
            onClickCancel={handleOpenModal}
            onSave={handleOnSave}
          />
        ) : (
          <Fragment>
            <Typography fontSize={'16px'} fontWeight={500}>
              Pricing Table Block
            </Typography>
            <Typography fontSize={'14px'} fontWeight={500} marginTop={'32px'}>
              Header Color
            </Typography>
            <Box display={'flex'} flexDirection={'column'} gap={'16px'} marginTop={'16px'} width={'100%'}>
              <input type='color' value={headerColor} onChange={event => handleOnChangeBackgroundColor(event)} />
            </Box>
            <Typography fontSize={'14px'} fontWeight={500} marginTop={'32px'}>
              Hide & Show Data
            </Typography>
            <Box display={'flex'} flexDirection={'column'} gap={'8px'} marginTop={'16px'}>
              <FormControlLabel
                control={
                  <Switch checked={!hideListPriceColumn} color='secondary' onChange={handleOnChangeHideListPrice} />
                }
                label='List Price'
              />
              <FormControlLabel
                control={
                  <Switch checked={!hideDiscountColumn} color='secondary' onChange={handleOnChangeHideDiscount} />
                }
                label='Discount'
              />
              <FormControlLabel
                control={
                  <Switch checked={!hideDescriptionColumn} color='secondary' onChange={handleOnChangeHideDescription} />
                }
                label='Description'
              />
              <FormControlLabel
                control={<Switch checked={!hideSummary} color='secondary' onChange={handleOnChangeHideSummary} />}
                label='Table Summary'
              />
            </Box>
            <Typography fontSize={'14px'} fontWeight={500} marginTop={'32px'}>
              Show Description Content As Text
            </Typography>
            <Box display={'flex'} flexDirection={'column'} gap={'8px'} marginTop={'16px'}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!showDescriptionAsImage}
                    color='secondary'
                    onChange={handleOnChangeShowDescriptionAsAnImage}
                  />
                }
                label='Show as Text'
              />
            </Box>
            <Divider sx={{ color: '#C9CACB', width: '100%', marginY: '32px' }} />
            <Typography fontSize={'14px'} fontWeight={500}>
              Custom Headers
            </Typography>
            <Box
              display={'flex'}
              flexDirection={'column'}
              alignItems={'flex-start'}
              gap={'16px'}
              marginTop={'16px'}
              width={'100%'}
            >
              <Typography fontSize={'14px'} fontWeight={400}>
                Product
              </Typography>
              <TextField
                value={columns.find(col => col.id == 'column-1')?.header}
                fullWidth
                type='text'
                sx={{
                  ...commonTextFieldStyles,
                }}
                onChange={e => handleOnChangeHeaderText(e.target.value, 'column-1')}
              />
              <Typography fontSize={'14px'} fontWeight={400}>
                List Price
              </Typography>
              <TextField
                value={columns.find(col => col.id == 'column-2')?.header}
                fullWidth
                type='text'
                sx={{
                  ...commonTextFieldStyles,
                }}
                onChange={e => handleOnChangeHeaderText(e.target.value, 'column-2')}
              />
              <Typography fontSize={'14px'} fontWeight={400}>
                Sales Price
              </Typography>
              <TextField
                value={columns.find(col => col.id == 'column-3')?.header}
                fullWidth
                type='text'
                sx={{
                  ...commonTextFieldStyles,
                }}
                onChange={e => handleOnChangeHeaderText(e.target.value, 'column-3')}
              />
              <Typography fontSize={'14px'} fontWeight={400}>
                Qty.
              </Typography>
              <TextField
                value={columns.find(col => col.id == 'column-4')?.header}
                fullWidth
                type='text'
                sx={{
                  ...commonTextFieldStyles,
                }}
                onChange={e => handleOnChangeHeaderText(e.target.value, 'column-4')}
              />
              <Typography fontSize={'14px'} fontWeight={400}>
                Dis. (%)
              </Typography>
              <TextField
                value={columns.find(col => col.id == 'column-5')?.header}
                fullWidth
                type='text'
                sx={{
                  ...commonTextFieldStyles,
                }}
                onChange={e => handleOnChangeHeaderText(e.target.value, 'column-5')}
              />
              <Typography fontSize={'14px'} fontWeight={400}>
                Total Price
              </Typography>
              <TextField
                value={columns.find(col => col.id == 'column-6')?.header}
                fullWidth
                type='text'
                sx={{
                  ...commonTextFieldStyles,
                }}
                onChange={e => handleOnChangeHeaderText(e.target.value, 'column-6')}
              />
              <Typography fontSize={'14px'} fontWeight={400}>
                Description
              </Typography>
              <TextField
                value={columns.find(col => col.id == 'column-7')?.header}
                fullWidth
                type='text'
                sx={{
                  ...commonTextFieldStyles,
                  marginBottom: '32px',
                }}
                onChange={e => handleOnChangeHeaderText(e.target.value, 'column-7')}
              />
            </Box>
          </Fragment>
        )}
      </Box>
      <Modal open={openModal} onClose={handleCloseModal} aria-labelledby='discard-changes' closeAfterTransition>
        <Fade in={openModal}>
          <Box className={styles.modalContainer}>
            <Box alignItems={'center'} display={'flex'} width={'100%'} justifyContent={'space-between'} mb={'24px'}>
              <Typography fontSize={'24px'} fontWeight={'500'} color={'#474747'}>
                Discard Changes?
              </Typography>
              <IconButton aria-label='close-modal' onClick={handleCloseModal}>
                <CloseRounded sx={{ color: docGeniusPalette.primaryBlue }} />
              </IconButton>
            </Box>
            <Typography fontSize={'16px'} fontWeight={'400'} lineHeight='24px'>
              By doing this, the information entered will be lost.
            </Typography>
            <Box alignItems={'center'} display={'flex'} justifyContent={'space-between'} mt={'64px'}>
              <Button variant='secondary' onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button onClick={handleOnClickDiscardChanges}>Discard Changes</Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Fragment>
  );
}
